const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export interface ReserveTable {
  id: string;
  number?: number;
  name?: string;
  seatingCapacity?: number;
  sectionId?: string;
  sectionName?: string;
  terminalGroupId?: string;
}

export interface ReserveSection {
  id: string;
  terminalGroupId?: string;
  name: string;
  tables: ReserveTable[];
}

export interface AvailabilityResponse {
  success: boolean;
  freeTables: ReserveTable[];
  busyTableIds: string[];
  sections: ReserveSection[];
}

export interface BookingDateOption {
  date: string;
  label: string;
  weekday: string;
}

export interface DaySlot {
  time: string;
  freeCount: number;
  available: boolean;
}

export interface DaySlotsResponse {
  success: boolean;
  dates: BookingDateOption[];
  date: string;
  slots: DaySlot[];
  availableSlots: DaySlot[];
  warning?: string;
  terminalGroupsCount?: number;
  sectionsCount?: number;
  tablesCount?: number;
}

export interface CreateReservePayload {
  phone: string;
  customerName: string;
  guestsCount: number;
  estimatedStartTime: string;
  durationInMinutes?: number;
  tableIds?: string[];
  terminalGroupId?: string;
  comment?: string;
}

export interface CreateReserveResult {
  success: boolean;
  requestId: string;
  status: string;
  estimatedStartTime: string;
  durationInMinutes: number;
  guestsCount: number;
}

function toIikoDateTime(date: string, time: string): string {
  // date: YYYY-MM-DD, time: HH:mm
  return `${date} ${time}:00.000`;
}

async function readError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    return msg || data?.error || `Ошибка ${res.status}`;
  } catch {
    return `Ошибка ${res.status}`;
  }
}

export const reservesApi = {
  toIikoDateTime,

  async getSections(): Promise<{ sections: ReserveSection[] }> {
    const res = await fetch(`${API_URL}/reserves/sections`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  },

  async getDaySlots(params: {
    date: string;
    guestsCount: number;
    durationInMinutes?: number;
  }): Promise<DaySlotsResponse> {
    const url = new URL(`${API_URL}/reserves/day-slots`);
    url.searchParams.set('date', params.date);
    url.searchParams.set('guestsCount', String(params.guestsCount));
    if (params.durationInMinutes) {
      url.searchParams.set('durationInMinutes', String(params.durationInMinutes));
    }
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  },

  async getAvailability(params: {
    date: string;
    time: string;
    guestsCount: number;
    durationInMinutes?: number;
  }): Promise<AvailabilityResponse> {
    const url = new URL(`${API_URL}/reserves/availability`);
    url.searchParams.set('estimatedStartTime', toIikoDateTime(params.date, params.time));
    url.searchParams.set('guestsCount', String(params.guestsCount));
    if (params.durationInMinutes) {
      url.searchParams.set('durationInMinutes', String(params.durationInMinutes));
    }
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  },

  async create(payload: CreateReservePayload): Promise<CreateReserveResult> {
    const res = await fetch(`${API_URL}/reserves`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  },
};
